// Éditeur de mise en page — plein écran (docs/REFONTE-CHARTES.md §3 et §4).
//
// L'éditeur ne règle que la STRUCTURE : lignes, colonnes, blocs, traits, marges, liens. Aucun style
// de bloc : couleur, taille, graisse, interlettrage, interligne appartiennent à la charte, par rôle
// (fiche, tableau des styles). Le panneau d'un bloc montre son rôle en lecture seule et renvoie à la
// fiche.
//
// Règle §0 : le canevas EST la signature rendue par renderSignature() — le même HTML que celui poussé
// dans Gmail. Ce fichier n'a aucun rendu à lui : il pose par-dessus une couche d'interaction
// (surlignage, sélection, marqueurs d'insertion, poignées) et appelle les mutations pures de
// core/layout-edit.ts. Le lien entre les cellules du DOM et l'arbre passe par la carte `rendered`
// que le moteur renvoie à côté du HTML : rien n'est annoté dans le HTML lui-même.
//
// Dispositions (§4) : ≥ 1 200 px de fenêtre, trois colonnes ; 900–1 200, palette en bandeau au-dessus
// du canevas et panneau en tiroir flottant ; < 900, empilé (canevas pleine largeur, puis deux onglets
// Blocs / Propriétés). Les libellés de la palette sont toujours complets (jamais d'abréviation).
// Un clic sur un bloc de la palette l'ajoute en fin de la colonne sélectionnée (ou de la colonne
// principale) : le glisser-déposer, peu fiable dans certains navigateurs, n'est qu'un complément.
// Le canevas n'est jamais rogné : zoom « ajuster » (propriété CSS zoom) et défilement en repli.
import { busy, h, mount, toast } from '../dom.ts';
import {
  appendBlock,
  blockLabel,
  blockRole,
  type BorderSide,
  type Brand,
  canRedo,
  canSplit,
  canUndo,
  columnMinWidth,
  fitLayoutWidths,
  mediaColumnWidth,
  commit,
  createHistory,
  type DropTarget,
  effectiveLayout,
  effectiveStyles,
  findBlock,
  findCol,
  type History,
  insertBlock,
  type LayoutBlock,
  type LayoutCol,
  layoutFromLegacy,
  listCols,
  makeBlock,
  moveBlockBy,
  moveBlockTo,
  moveBlockToCol,
  PALETTE,
  type PaletteKey,
  redo,
  removeBlock,
  removeCol,
  removeColBorder,
  removeRow,
  renderSignature,
  resizeColumns,
  roleColor,
  roleLabel,
  roleSummary,
  textRoleOptions,
  setColBorder,
  type SignatureLayout,
  SPLIT_REFUSED_NOTE,
  socialLinks,
  SOCIAL_NETWORKS,
  TEXT_VARIABLES,
  unavailableKeys,
  undo,
  updateBlock,
  updateCol,
} from '../../../supabase/functions/_shared/core/index.ts';
import { publicBaseUrl } from '../config.ts';
import { reload, type Store } from '../state.ts';
import { signatureMeters } from './meters.ts';

/** Route de l'éditeur : #chartes/<id>/mise-en-page */
export const EDITOR_ROUTE = /^chartes\/([a-z0-9-]+)\/mise-en-page$/;

type Selection = { kind: 'block' | 'col' | 'row'; id: string } | null;
interface CanvasNode {
  kind: 'block' | 'col' | 'row';
  id: string;
  el: HTMLElement;
  colId?: string;
  rowIndex?: number;
}

const MIN_COL = 24;
const EDGE = 14; // px : zone de scission sur le bord d'une colonne
const THREE_COLS = 1200; // px de fenêtre : palette | canevas | panneau
const DRAWER = 900; // px de fenêtre : en dessous, empilé ; entre les deux, tiroir
const STYLE_NOTE = 'Le style se règle une fois, par rôle, pour toute la charte. Un bloc ne peut pas y déroger : c’est ce qui garantit des signatures uniformes.';
const STYLE_NOTE_SHORT = 'Couleurs, tailles et graisses ne se règlent pas ici : elles appartiennent à la charte, par rôle.';

export function layoutEditorView(store: Store, brandId: string): HTMLElement {
  const brand = store.brands.find((b) => b.id === brandId);
  if (!brand) return h('div', { class: 'panel' }, h('p', null, 'Charte introuvable.'), h('a', { class: 'button', href: '#chartes' }, 'Retour aux chartes'));
  const styles = effectiveStyles(brand);
  /** Largeur minimale d'une colonne : jamais sous son contenu (logo, icônes, image…), ni sous MIN_COL. */
  const colMin = (c: LayoutCol | undefined) => Math.max(MIN_COL, c ? columnMinWidth(brand, styles, c) : 0);
  const person = store.people.find((p) => p.kind === 'person') ?? store.people[0];
  const sheetUrl = `#chartes/${brand.id}`;

  // Colonnes média (logo, image) : largeur calculée depuis la fiche et les marges, jamais saisie (08/10).
  // Si l'arbre enregistré en déclare une autre, on la corrige à l'ouverture et « Terminer » l'écrit.
  const stored = effectiveLayout(brand);
  const fitted = fitLayoutWidths(brand, styles, stored);
  const initial = fitted.layout;
  let history: History<SignatureLayout> = createHistory(initial);
  const layout = () => history.present;
  const savedJson = JSON.stringify(stored);
  if (fitted.corrections.length) {
    setTimeout(() => toast(`Colonne du logo mise d’équerre : ${fitted.corrections.map((c) => c.kind === 'raise' ? `ligne ${c.rowIndex}, colonne ${c.colIndex} déclarée ${c.from} px pour un contenu de ${c.to} px, relevée à ${c.to} px` : `ligne ${c.rowIndex}, colonne ${c.colIndex} : ${Number(c.from) - (c.to - (c.gutter ?? 0))} px d’espace à côté du logo deviennent une marge de colonne de ${c.gutter} px (largeur ${c.to} px inchangée)`).join(' ; ')}. Cliquez « Terminer » pour l’enregistrer.`), 0);
  }
  const dirty = () => JSON.stringify(layout()) !== savedJson;
  let selection: Selection = null;
  let dragging: { kind: 'block'; id: string } | { kind: 'palette'; key: PaletteKey } | null = null;
  let nodes: CanvasNode[] = [];
  let renderedBlocks = new Set<string>();
  let fit = true;
  let zoom = 1;
  let mode: 'three' | 'drawer' | 'stacked' = 'three';
  let drawerOpen = false;
  let stackedTab: 'blocks' | 'props' = 'blocks';
  let lastHtml = '';

  // ------------------------------------------------------------- squelette
  const palette = h('div', { class: 'ed-palette' });
  const stage = h('div', { class: 'ed-stage', tabindex: 0 });
  const canvas = h('div', { class: 'ed-canvas' });
  const overlay = h('div', { class: 'ed-overlay' });
  const widthLabel = h('div', { class: 'ed-width-label' });
  const gestures = h('div', { class: 'ed-gestures' }, h('b', null, 'Clic'), ' : un bloc · ', h('b', null, 'Double-clic'), ' : sa colonne · ', h('b', null, 'Clic dans la marge'), ' : la colonne · Le panneau affiche ', h('b', null, 'Ligne › Colonne › bloc'), ', chaque maillon se clique.');
  const stageWrap = h('div', { class: 'ed-stage-wrap' }, widthLabel, stage, gestures);
  const panel = h('div', { class: 'ed-panel' });
  const tabs = h('div', { class: 'ed-tabs' });
  const body = h('div', { class: 'ed-body' }, palette, stageWrap, panel);
  const bar = h('div', { class: 'ed-bar' });
  const el = h('div', { class: 'ed' }, bar, body);
  stage.append(canvas, overlay);

  const meters = signatureMeters({ short: () => mode !== 'three' });
  const undoBtn = h('button', { type: 'button', class: 'ghost', title: 'Annuler (⌘Z)', onclick: () => doUndo() }, 'Annuler');
  const redoBtn = h('button', { type: 'button', class: 'ghost', title: 'Rétablir (⇧⌘Z)', onclick: () => doRedo() }, 'Rétablir');
  const zoomOut = h('button', { type: 'button', class: 'ghost', title: 'Réduire', onclick: () => setZoom(zoom - 0.1) }, '−');
  const zoomIn = h('button', { type: 'button', class: 'ghost', title: 'Agrandir', onclick: () => setZoom(zoom + 0.1) }, '+');
  const zoomLabel = h('span', { class: 'ed-zoom' });
  const fitBtn = h('button', { type: 'button', class: 'ghost', title: 'Ajuster le canevas à la largeur disponible', onclick: () => { fit = !fit; layoutChrome(); } }, 'Ajuster');
  const dirtyDot = h('span', { class: 'ed-dirty', title: 'Modifications non enregistrées' });
  const backBtn = h('button', { type: 'button', class: 'ghost ed-back', title: 'Fiche de la charte', onclick: () => leave() }, '← ', h('span', { class: 'ed-back-label' }, 'Fiche de la charte'));
  const doneBtn = h('button', { type: 'button', onclick: () => finish() }, 'Terminer');
  const panelToggle = h('button', { type: 'button', class: 'ghost ed-panel-toggle', onclick: () => { drawerOpen = !drawerOpen; layoutChrome(); } }, 'Propriétés');
  const moreBody = h('div', { class: 'ed-more-body' });
  const more = h('details', { class: 'ed-more' }, h('summary', { title: 'Autres réglages' }, '⋯'), moreBody);
  // − / + / Ajuster : dans la barre, ou dans le menu ⋯ en mode empilé (le zoom lui-même reste visible)
  const zoomCtl = h('span', { class: 'ed-inline ed-zoom-ctl' }, zoomOut, zoomIn, fitBtn);
  const barCenter = h('span', { class: 'ed-bar-center' }, undoBtn, redoBtn, zoomLabel, zoomCtl);
  mount(
    bar,
    h('span', { class: 'ed-bar-left' }, backBtn, h('span', { class: 'ed-brand-name' }, brand.name), dirtyDot),
    barCenter,
    h('span', { class: 'ed-bar-right' }, meters.el, panelToggle, doneBtn, more),
  );
  mount(moreBody, h('div', { class: 'ed-brand-name-more' }, brand.name));
  document.addEventListener('click', (e) => {
    if (more.open && !more.contains(e.target as Node)) more.open = false;
  });

  // ------------------------------------------------------------- sortie
  const leave = () => {
    if (dirty() && !confirm('Quitter sans enregistrer la mise en page ? Les modifications seront perdues.')) return;
    location.hash = sheetUrl;
  };
  const finish = () =>
    busy(doneBtn, async () => {
      if (dirty()) {
        // Seule la mise en page est écrite : les styles (fiche) enregistrés entre-temps ne sont pas écrasés.
        await store.api.saveBrandLayout(brand.id, layout());
        await reload(store);
        toast(store.api.demo ? 'Mise en page enregistrée (démo)' : 'Mise en page enregistrée — appliquée à la prochaine poussée (≤ 15 min)');
      }
      location.hash = sheetUrl;
    });
  const beforeUnload = (e: BeforeUnloadEvent) => {
    if (dirty()) e.preventDefault();
  };
  window.addEventListener('beforeunload', beforeUnload);

  // ------------------------------------------------------------- pile d'annulation
  const apply = (next: SignatureLayout, key: string | null = null) => {
    history = commit(history, next, key);
    refresh();
  };
  const stillExists = (sel: NonNullable<Selection>) =>
    sel.kind === 'block' ? !!findBlock(layout(), sel.id) : sel.kind === 'col' ? !!findCol(layout(), sel.id) : layout().rows.some((r) => r.id === sel.id);
  const doUndo = () => {
    if (!canUndo(history)) return;
    history = undo(history);
    selection = selection && stillExists(selection) ? selection : null;
    refresh();
  };
  const doRedo = () => {
    if (!canRedo(history)) return;
    history = redo(history);
    selection = selection && stillExists(selection) ? selection : null;
    refresh();
  };

  // ------------------------------------------------------------- disposition (largeur, zoom, tiroir, onglets)
  const setZoom = (z: number) => {
    fit = false;
    zoom = Math.min(2, Math.max(0.3, Math.round(z * 10) / 10));
    layoutChrome();
  };
  let retry = 0;
  const layoutChrome = () => {
    if (!el.isConnected || !el.clientWidth) {
      if (retry++ < 60) requestAnimationFrame(layoutChrome);
      return;
    }
    retry = 0;
    const w = window.innerWidth;
    const next: typeof mode = w >= THREE_COLS ? 'three' : w >= DRAWER ? 'drawer' : 'stacked';
    if (next !== mode || !body.contains(stageWrap)) {
      mode = next;
      if (mode === 'stacked') {
        body.append(stageWrap, tabs, palette, panel);
        moreBody.append(zoomCtl);
      } else {
        body.append(palette, stageWrap, panel); // en tiroir, la palette est un bandeau au-dessus (CSS)
        barCenter.append(zoomCtl);
      }
      more.open = false;
      if (lastHtml) meters.update(lastHtml); // compteur abrégé hors trois colonnes
      drawPalette();
      drawPanel();
      drawTabs();
    }
    el.classList.toggle('three', mode === 'three');
    el.classList.toggle('drawer', mode === 'drawer');
    el.classList.toggle('stacked', mode === 'stacked');
    el.classList.toggle('drawer-open', mode === 'drawer' && drawerOpen);
    el.classList.toggle('tab-blocks', mode === 'stacked' && stackedTab === 'blocks');
    el.classList.toggle('tab-props', mode === 'stacked' && stackedTab === 'props');
    // zoom « ajuster » : le canevas entier tient dans la zone, jamais rogné (marges lues, pas devinées)
    const px = (v: string) => parseFloat(v) || 0;
    const wrapCs = getComputedStyle(stageWrap);
    const stageCs = getComputedStyle(stage);
    const available = stageWrap.clientWidth - px(wrapCs.paddingLeft) - px(wrapCs.paddingRight);
    const wanted = layout().width + px(stageCs.paddingLeft) + px(stageCs.paddingRight);
    if (fit) zoom = Math.min(1, Math.max(0.3, available / wanted));
    (stage.style as CSSStyleDeclaration & { zoom?: string }).zoom = String(zoom);
    zoomLabel.textContent = `${Math.round(zoom * 100)} %`;
    fitBtn.classList.toggle('on', fit);
    widthLabel.textContent = `Gabarit ${layout().width} px`;
    dirtyDot.classList.toggle('on', dirty());
    drawOverlay();
  };
  const resizeObserver = new ResizeObserver(() => layoutChrome());
  resizeObserver.observe(el);
  resizeObserver.observe(stageWrap);
  window.addEventListener('resize', layoutChrome);
  // filet : certains navigateurs embarqués ne signalent pas les redimensionnements
  let lastWidth = 0;
  const poll = setInterval(() => {
    if (!el.isConnected) {
      clearInterval(poll);
      resizeObserver.disconnect();
      window.removeEventListener('resize', layoutChrome);
      window.removeEventListener('beforeunload', beforeUnload);
      return;
    }
    if (window.innerWidth !== lastWidth) {
      lastWidth = window.innerWidth;
      layoutChrome();
    }
  }, 400);

  const drawTabs = () => {
    if (mode !== 'stacked') return mount(tabs);
    const tab = (id: typeof stackedTab, label: string) =>
      h('button', { type: 'button', class: `ghost${stackedTab === id ? ' on' : ''}`, onclick: () => { stackedTab = id; layoutChrome(); drawTabs(); } }, label);
    mount(tabs, tab('blocks', 'Blocs'), tab('props', selection ? `Propriétés — ${selectionLabel()}` : 'Propriétés'));
  };
  const selectionLabel = () => {
    const sel = selection;
    if (!sel) return '';
    if (sel.kind === 'block') return blockLabel(findBlock(layout(), sel.id)?.block ?? { id: '', type: 'text', text: '' });
    return sel.kind === 'col' ? 'colonne' : 'ligne';
  };

  // ------------------------------------------------------------- rendu et cartographie du DOM
  const refresh = () => {
    nodes = [];
    renderedBlocks = new Set();
    undoBtn.disabled = !canUndo(history);
    redoBtn.disabled = !canRedo(history);
    if (!person) {
      canvas.innerHTML = '';
      mount(overlay, h('p', { class: 'muted' }, 'Ajoutez une personne pour éditer la mise en page.'));
      return;
    }
    const r = renderSignature(
      { person: { ...person, brand_id: brand.id }, sendAsEmail: person.primary_email, brands: [{ ...brand, layout_tree: layout() }], campaigns: store.campaigns },
      { mode: 'gmail', publicBaseUrl },
    );
    if (r.html !== lastHtml) {
      canvas.innerHTML = r.html; // le HTML poussé, tel quel
      lastHtml = r.html;
    }
    meters.update(r.html);

    const outer = canvas.querySelector(':scope > table');
    const rowTrs = outer ? [...outer.querySelectorAll(':scope > tbody > tr')] : [];
    r.rendered.rows.forEach((row, i) => {
      const tr = rowTrs[i];
      const rowTable = tr?.querySelector(':scope > td > table') as HTMLElement | null;
      if (!rowTable) return;
      nodes.push({ kind: 'row', id: row.id, el: rowTable, rowIndex: i });
      const colTds = [...rowTable.querySelectorAll(':scope > tbody > tr > td')] as HTMLElement[];
      row.cols.forEach((col, j) => {
        const td = colTds[j];
        if (!td) return;
        nodes.push({ kind: 'col', id: col.id, el: td, rowIndex: i });
        if (!col.blocks.length) return;
        const blocksTable = td.querySelector(':scope > table');
        const blockTds = blocksTable ? ([...blocksTable.querySelectorAll(':scope > tbody > tr > td')] as HTMLElement[]) : [];
        col.blocks.forEach((id, k) => {
          const btd = blockTds[k];
          if (!btd) return;
          renderedBlocks.add(id);
          if (!btd.draggable) {
            btd.draggable = true; // couche d'interaction : le DOM du canevas, jamais le HTML produit
            btd.addEventListener('dragstart', (e) => {
              dragging = { kind: 'block', id };
              e.dataTransfer?.setData('text/plain', id);
              select({ kind: 'block', id });
            });
            btd.addEventListener('dragend', () => {
              dragging = null;
              clearMarkers();
            });
          }
          nodes.push({ kind: 'block', id, el: btd, colId: col.id });
        });
      });
    });
    if (selection && !nodeFor(selection) && !stillExists(selection)) selection = null;
    layoutChrome();
    drawPalette();
    drawPanel();
    drawTabs();
  };

  const nodeFor = (sel: Selection) => (sel ? nodes.find((n) => n.kind === sel.kind && n.id === sel.id) ?? null : null);
  /** Rectangle d'un nœud dans le repère du canevas (mesures et souris sont toutes deux affectées par le zoom : cohérent). */
  const rectOf = (n: CanvasNode) => {
    const a = n.el.getBoundingClientRect();
    const b = stage.getBoundingClientRect();
    return { x: (a.left - b.left) / zoom, y: (a.top - b.top) / zoom, w: a.width / zoom, h: a.height / zoom };
  };

  // ------------------------------------------------------------- couche d'interaction
  let hovered: CanvasNode | null = null;
  const drawOverlay = () => {
    overlay.replaceChildren();
    const box = (n: CanvasNode, cls: string, label?: string) => {
      const r = rectOf(n);
      const d = h('div', { class: `ed-box ${cls}`, style: `left:${r.x}px;top:${r.y}px;width:${r.w}px;height:${r.h}px` });
      if (label) d.append(h('span', { class: 'ed-tag' }, label));
      return d;
    };
    if (hovered && !(selection && hovered.kind === selection.kind && hovered.id === selection.id)) overlay.append(box(hovered, 'hover'));
    const sel = nodeFor(selection);
    if (sel) {
      const label = sel.kind === 'block' ? blockLabel(findBlock(layout(), sel.id)!.block) : sel.kind === 'col' ? 'Colonne' : 'Ligne';
      const b = box(sel, 'selected', label);
      b.append(h('button', { type: 'button', class: 'ed-del', title: 'Retirer (Suppr) — annulable avec ⌘Z', onclick: (e: Event) => { e.stopPropagation(); deleteSelection(); } }, '×'));
      overlay.append(b);
    }
    for (const rowNode of nodes.filter((n) => n.kind === 'row')) {
      const cols = nodes.filter((n) => n.kind === 'col' && n.rowIndex === rowNode.rowIndex);
      for (let j = 0; j < cols.length - 1; j++) {
        const r = rectOf(cols[j]);
        const handle = h('div', { class: 'ed-handle', style: `left:${r.x + r.w - 3}px;top:${r.y}px;height:${r.h}px`, title: 'Redimensionner les colonnes' });
        // Pas de poignée quand l'une des deux colonnes est une colonne média : sa largeur est calculée.
        const colL = findCol(layout(), cols[j].id)?.col, colR = findCol(layout(), cols[j + 1].id)?.col;
        if (!(colL && mediaColumnWidth(brand, styles, colL) !== null) && !(colR && mediaColumnWidth(brand, styles, colR) !== null)) {
          handle.addEventListener('mousedown', (e) => startResize(e, cols[j].id, cols[j + 1].id));
        } else handle.classList.add('fixed');
        overlay.append(handle);
      }
    }
  };

  const select = (sel: Selection) => {
    selection = sel;
    if (sel && mode === 'drawer') drawerOpen = true;
    if (sel && mode === 'stacked') stackedTab = 'props';
    layoutChrome();
    drawPanel();
    drawTabs();
  };

  const nodeAt = (x: number, y: number): CanvasNode | null => {
    const under = (kind: CanvasNode['kind']) =>
      nodes.filter((n) => n.kind === kind).find((n) => {
        const r = rectOf(n);
        return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
      }) ?? null;
    return under('block') ?? under('col') ?? under('row');
  };
  const stagePoint = (e: MouseEvent) => {
    const b = stage.getBoundingClientRect();
    return { x: (e.clientX - b.left) / zoom, y: (e.clientY - b.top) / zoom };
  };

  stage.addEventListener('mousemove', (e) => {
    if (dragging || resizing) return;
    const p = stagePoint(e);
    const n = nodeAt(p.x, p.y);
    if (n !== hovered) {
      hovered = n;
      drawOverlay();
    }
  });
  stage.addEventListener('mouseleave', () => {
    hovered = null;
    drawOverlay();
  });
  stage.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('.ed-del')) return;
    const p = stagePoint(e);
    const n = nodeAt(p.x, p.y);
    select(n ? { kind: n.kind, id: n.id } : null);
    stage.focus({ preventScroll: true });
  });
  stage.addEventListener('dblclick', (e) => {
    const p = stagePoint(e);
    const n = nodeAt(p.x, p.y);
    if (n?.kind === 'block' && n.colId) select({ kind: 'col', id: n.colId });
  });
  el.addEventListener('keydown', (e) => {
    const inField = (e.target as HTMLElement).matches('input,select,textarea');
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'z' && !inField) {
      e.preventDefault();
      if (e.shiftKey) doRedo();
      else doUndo();
      return;
    }
    if (e.key === 'Escape') select(null);
    if ((e.key === 'Delete' || e.key === 'Backspace') && selection && !inField) {
      e.preventDefault();
      deleteSelection();
    }
  });

  const deleteSelection = () => {
    if (!selection) return;
    const { kind, id } = selection;
    selection = null;
    apply(kind === 'block' ? removeBlock(layout(), id) : kind === 'col' ? removeCol(layout(), id) : removeRow(layout(), id));
  };

  // ------------------------------------------------------------- glisser-déposer : cibles et marqueurs
  const clearMarkers = () => overlay.querySelectorAll('.ed-marker').forEach((m) => m.remove());

  const targetAt = (x: number, y: number): { target: DropTarget; marker: { x: number; y: number; w: number; h: number } } | null => {
    for (const c of nodes.filter((n) => n.kind === 'col')) {
      const r = rectOf(c);
      if (y < r.y || y > r.y + r.h) continue;
      if (Math.abs(x - r.x) <= EDGE) return { target: { kind: 'split', colId: c.id, side: 'left' }, marker: { x: r.x - 1, y: r.y, w: 3, h: r.h } };
      if (Math.abs(x - (r.x + r.w)) <= EDGE) return { target: { kind: 'split', colId: c.id, side: 'right' }, marker: { x: r.x + r.w - 2, y: r.y, w: 3, h: r.h } };
    }
    for (const c of nodes.filter((n) => n.kind === 'col')) {
      const r = rectOf(c);
      if (x < r.x || x > r.x + r.w || y < r.y || y > r.y + r.h) continue;
      const col = findCol(layout(), c.id)?.col;
      if (!col) continue;
      const blocks = nodes.filter((n) => n.kind === 'block' && n.colId === c.id);
      if (!blocks.length) return { target: { kind: 'block', colId: c.id, index: col.blocks.length }, marker: { x: r.x, y: r.y + 2, w: r.w, h: 3 } };
      let best: { d: number; index: number; y: number } | null = null;
      for (const b of blocks) {
        const br = rectOf(b);
        const idx = col.blocks.findIndex((bl) => bl.id === b.id);
        for (const [edgeY, index] of [[br.y, idx], [br.y + br.h, idx + 1]] as [number, number][]) {
          const d = Math.abs(y - edgeY);
          if (!best || d < best.d) best = { d, index, y: edgeY };
        }
      }
      return best ? { target: { kind: 'block', colId: c.id, index: best.index }, marker: { x: r.x, y: best.y - 1, w: r.w, h: 3 } } : null;
    }
    const rows = nodes.filter((n) => n.kind === 'row');
    if (!rows.length) return { target: { kind: 'row', index: 0 }, marker: { x: 0, y: 0, w: canvas.clientWidth, h: 3 } };
    let bestRow: { d: number; index: number; y: number } | null = null;
    for (const [i, rn] of rows.entries()) {
      const r = rectOf(rn);
      for (const [edgeY, index] of [[r.y, i], [r.y + r.h, i + 1]] as [number, number][]) {
        const d = Math.abs(y - edgeY);
        if (!bestRow || d < bestRow.d) bestRow = { d, index, y: edgeY };
      }
    }
    if (!bestRow) return null;
    const first = rectOf(rows[0]);
    return { target: { kind: 'row', index: bestRow.index }, marker: { x: first.x, y: bestRow.y - 1, w: first.w, h: 3 } };
  };

  stage.addEventListener('dragover', (e) => {
    if (!dragging) return;
    e.preventDefault();
    const p = stagePoint(e);
    const t = targetAt(p.x, p.y);
    clearMarkers();
    if (!t) return;
    const refused = t.target.kind === 'split' && !canSplit(layout(), t.target.colId);
    overlay.append(h('div', { class: `ed-marker${refused ? ' refused' : ''}`, style: `left:${t.marker.x}px;top:${t.marker.y}px;width:${t.marker.w}px;height:${t.marker.h}px` }));
  });
  stage.addEventListener('dragleave', (e) => {
    if (!stage.contains(e.relatedTarget as Node | null)) clearMarkers();
  });
  stage.addEventListener('drop', (e) => {
    e.preventDefault();
    if (!dragging) return;
    const p = stagePoint(e);
    const t = targetAt(p.x, p.y);
    const d = dragging;
    dragging = null;
    clearMarkers();
    if (!t) return;
    if (t.target.kind === 'split' && !canSplit(layout(), t.target.colId)) {
      toast(SPLIT_REFUSED_NOTE, 'error');
      return;
    }
    if (d.kind === 'block') {
      apply(moveBlockTo(layout(), d.id, t.target));
      selection = { kind: 'block', id: d.id };
    } else if (!unavailableKeys(layout()).has(d.key)) {
      const block = makeBlock(layout(), d.key);
      apply(insertBlock(layout(), block, t.target));
      selection = { kind: 'block', id: block.id };
    }
    layoutChrome();
    drawPanel();
    drawTabs();
  });

  // ------------------------------------------------------------- redimensionnement des colonnes
  let resizing: { leftId: string; rightId: string; x0: number; left0: number; right0: number | 'auto'; start: SignatureLayout } | null = null;
  const startResize = (e: MouseEvent, leftId: string, rightId: string) => {
    e.preventDefault();
    const left = nodes.find((n) => n.kind === 'col' && n.id === leftId)!;
    const rightCol = findCol(layout(), rightId)?.col;
    resizing = { leftId, rightId, x0: e.clientX, left0: rectOf(left).w, right0: rightCol?.width === 'auto' ? 'auto' : rectOf(nodes.find((n) => n.kind === 'col' && n.id === rightId)!).w, start: layout() };
    document.body.classList.add('ed-resizing');
    let live = layout();
    const move = (ev: MouseEvent) => {
      if (!resizing) return;
      const dx = (ev.clientX - resizing.x0) / zoom;
      const leftPx = Math.max(colMin(findCol(resizing.start, resizing.leftId)?.col), resizing.left0 + dx);
      const rightPx = resizing.right0 === 'auto' ? 'auto' : Math.max(colMin(findCol(resizing.start, resizing.rightId)?.col), resizing.right0 - dx);
      live = resizeColumns(resizing.start, resizing.leftId, resizing.rightId, leftPx, rightPx);
      history = { ...history, present: live }; // aperçu en direct, sans entrée d'historique
      refresh();
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.body.classList.remove('ed-resizing');
      if (resizing) {
        history = { ...history, present: resizing.start };
        apply(live, `resize:${resizing.leftId}`); // une seule entrée d'historique par glissement
      }
      resizing = null;
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  // ------------------------------------------------------------- palette (les blocs, libellés complets)
  /** Colonne où un clic dans la palette ajoute le bloc : la colonne sélectionnée, celle du bloc sélectionné, sinon la principale. */
  const targetColId = (): string | null => {
    if (selection?.kind === 'col') return selection.id;
    if (selection?.kind === 'block') return findBlock(layout(), selection.id)?.col.id ?? null;
    return null;
  };
  const addFromPalette = (key: PaletteKey) => {
    if (unavailableKeys(layout()).has(key)) return;
    const block = makeBlock(layout(), key);
    apply(appendBlock(layout(), block, targetColId()));
    select({ kind: 'block', id: block.id });
  };
  const drawPalette = () => {
    const taken = unavailableKeys(layout());
    mount(
      palette,
      h('div', { class: 'ed-palette-title' }, 'Blocs'),
      PALETTE.map(({ key, label }) => {
        const used = taken.has(key);
        const item = h('button', { type: 'button', class: `ghost ed-chip${used ? ' used' : ''}`, draggable: !used, disabled: used, title: used ? `${label} — déjà dans la mise en page` : `${label} — cliquer pour l’ajouter en fin de colonne, ou glisser vers le canevas`, onclick: () => addFromPalette(key) }, label);
        if (!used) {
          item.addEventListener('dragstart', (e) => {
            dragging = { kind: 'palette', key };
            e.dataTransfer?.setData('text/plain', key);
          });
          item.addEventListener('dragend', () => {
            dragging = null;
            clearMarkers();
          });
        }
        return item;
      }),
      h('small', { class: 'muted' }, 'Un clic ajoute le bloc en fin de la colonne sélectionnée (ou de la colonne principale). Vous pouvez aussi le glisser dans une colonne, entre deux lignes, ou sur le bord d’une colonne pour la scinder.'),
    );
  };
  /** Fil d'Ariane du panneau : Ligne › Colonne › bloc — chaque maillon sélectionne son niveau (toutes dispositions). */
  const crumbs = (rowId: string, rowIndex: number, colId: string | null, colIndex: number, leaf: string | null) => {
    const link = (label: string, sel: Selection) => h('button', { type: 'button', class: 'ghost ed-crumb', title: `Sélectionner : ${label}`, onclick: () => select(sel) }, label);
    return h(
      'div',
      { class: 'ed-crumbs' },
      link(`Ligne ${rowIndex + 1}`, { kind: 'row', id: rowId }),
      colId ? ' › ' : null,
      colId ? link(`Colonne ${colIndex + 1}`, { kind: 'col', id: colId }) : null,
      leaf ? ' › ' : null,
      leaf ? h('span', null, leaf) : null,
    );
  };

  // ------------------------------------------------------------- panneau de propriétés (structure seulement)
  const row = (label: string, input: HTMLElement, hint?: string) =>
    h('label', { class: 'ed-prop' }, h('span', null, label), input, hint ? h('small', { class: 'muted' }, hint) : null);
  const selectEl = (values: string[], labels: string[], current: string, onSet: (v: string) => void) => {
    const s = h('select', { onchange: (e: Event) => onSet((e.target as HTMLSelectElement).value) }, values.map((v, i) => h('option', { value: v }, labels[i])));
    s.value = current;
    return s;
  };
  /** Marges : une valeur (tous côtés), ou « Par côté… » pour quatre champs. */
  const paddingRows = (value: [number, number, number, number] | undefined, onSet: (p: [number, number, number, number] | undefined) => void) => {
    const p = value ?? [0, 0, 0, 0];
    const uniform = p.every((n) => n === p[0]);
    let perSide = !uniform;
    const box = h('div', { class: 'ed-prop' }, h('span', null, 'Marges (px)'));
    const draw = () => {
      const inputs = perSide
        ? ['haut', 'droite', 'bas', 'gauche'].map((t, i) =>
          h('input', {
            type: 'number',
            min: 0,
            max: 64,
            value: p[i],
            title: t,
            oninput: (e: Event) => {
              const next = [...p] as [number, number, number, number];
              next[i] = Math.max(0, Math.round(Number((e.target as HTMLInputElement).value) || 0));
              onSet(next.every((n) => n === 0) ? undefined : next);
            },
          })
        )
        : [h('input', {
          type: 'number',
          min: 0,
          max: 64,
          value: p[0],
          oninput: (e: Event) => {
            const n = Math.max(0, Math.round(Number((e.target as HTMLInputElement).value) || 0));
            onSet(n === 0 ? undefined : [n, n, n, n]);
          },
        })];
      const toggle = h('button', { type: 'button', class: 'ghost', onclick: () => { perSide = !perSide; draw(); } }, perSide ? 'Une valeur' : 'Par côté…');
      mount(box, h('span', null, 'Marges (px)'), h('span', { class: 'ed-inline' }, ...inputs, toggle), h('small', { class: 'muted' }, perSide ? 'haut, droite, bas, gauche' : 'Espace autour du contenu, sur les quatre côtés'));
    };
    draw();
    return box;
  };
  const closeBtn = () => (mode === 'drawer' ? h('button', { type: 'button', class: 'ghost ed-close', title: 'Fermer', onclick: () => { drawerOpen = false; layoutChrome(); } }, '×') : null);
  const stylesLink = (role?: string) => h('a', { class: 'button ghost', href: role ? `${sheetUrl}/role/${role}` : sheetUrl }, role ? 'Régler ce rôle dans la fiche →' : 'Styles de la charte →');

  const drawPanel = () => {
    const sel = selection;
    if (!sel) {
      const restart = (m: 'classic' | 'compact') => {
        if (!confirm(`Repartir de la mise en page ${m === 'classic' ? 'classique' : 'compacte'} ? La mise en page actuelle sera remplacée (annulable avec ⌘Z).`)) return;
        apply(layoutFromLegacy({ ...brand, layout: m, layout_tree: null }));
      };
      const widthInput = h('input', {
        type: 'number',
        min: 320,
        max: 800,
        step: 10,
        value: layout().width,
        onchange: (e: Event) => apply({ ...layout(), width: Math.max(320, Math.min(800, Math.round(Number((e.target as HTMLInputElement).value) || 600))) }),
      });
      mount(
        panel,
        h('div', { class: 'ed-panel-title' }, 'Gabarit', closeBtn()),
        row('Largeur (px)', widthInput, 'Largeur totale de la signature'),
        h('div', { class: 'ed-prop' }, h('span', null, 'Modèles de départ'), h('span', { class: 'ed-inline' }, h('button', { type: 'button', class: 'ghost', onclick: () => restart('classic') }, 'Classique'), h('button', { type: 'button', class: 'ghost', onclick: () => restart('compact') }, 'Compacte'))),
        h('div', { class: 'ed-styles-note' }, stylesLink(), h('p', { class: 'muted' }, STYLE_NOTE_SHORT)),
        h('p', { class: 'muted' }, 'Cliquez un bloc du canevas ; sa colonne et sa ligne sont ensuite accessibles par le fil d’Ariane en haut du panneau (double-clic sur un bloc = sa colonne, clic dans la marge d’une colonne = la colonne).'),
      );
      return;
    }
    if (sel.kind === 'block') {
      const loc = findBlock(layout(), sel.id);
      if (!loc) return mount(panel, h('p', { class: 'muted' }, 'Bloc introuvable.'));
      const b = loc.block;
      const set = (patch: Partial<LayoutBlock>, key?: string) => apply(updateBlock(layout(), sel.id, patch), key ? `${sel.id}:${key}` : null);
      const role = blockRole(b);
      const cols = listCols(layout());
      mount(
        panel,
        h('div', { class: 'ed-panel-title' }, 'Ce bloc', h('small', { class: 'muted' }, ` ${blockLabel(b)}`), closeBtn()),
        crumbs(loc.row.id, loc.rowIndex, loc.col.id, loc.colIndex, blockLabel(b)),
        // Style : lecture seule, par rôle
        role
          ? h(
            'div',
            { class: 'ed-style' },
            h('span', null, 'Style'),
            h('div', { class: 'ed-style-role' }, h('i', { class: 'ed-swatch', style: `background:${roleColor(styles, role)}` }), h('span', null, `Rôle « ${roleLabel(styles, role)} »`)),
            h('div', { class: 'muted' }, roleSummary(styles, role)),
            stylesLink(role),
            h('p', { class: 'muted' }, STYLE_NOTE),
          )
          : h('div', { class: 'ed-style' }, h('span', null, 'Style'), h('div', { class: 'muted' }, 'Image : aucun style.')),
        // Emplacement
        h(
          'div',
          { class: 'ed-prop' },
          h('span', null, 'Emplacement'),
          h(
            'span',
            { class: 'ed-inline' },
            selectEl(cols.map((c) => c.id), cols.map((c) => c.label), loc.col.id, (v) => apply(moveBlockToCol(layout(), sel.id, v))),
            h('button', { type: 'button', class: 'ghost', title: 'Monter', disabled: loc.blockIndex === 0, onclick: () => apply(moveBlockBy(layout(), sel.id, -1)) }, '↑'),
            h('button', { type: 'button', class: 'ghost', title: 'Descendre', disabled: loc.blockIndex >= loc.col.blocks.length - 1, onclick: () => apply(moveBlockBy(layout(), sel.id, 1)) }, '↓'),
          ),
          h('small', { class: 'muted' }, `Position ${loc.blockIndex + 1} / ${loc.col.blocks.length} dans la colonne`),
        ),
        // Contenu : ce que le bloc dit — icônes gardées pour les réseaux, texte + variables + rôle pour le texte libre
        b.type === 'social'
          ? h(
            'div',
            { class: 'ed-prop' },
            h('span', null, 'Icônes affichées'),
            h(
              'div',
              { class: 'ed-checks' },
              SOCIAL_NETWORKS.map((n) => {
                const available = !!brand[n.field];
                const shown = available && (!b.networks || b.networks.includes(n.id));
                return h('label', { class: available ? '' : 'muted', title: available ? '' : 'Adresse non renseignée dans la fiche : jamais d’icône morte' }, h('input', {
                  type: 'checkbox',
                  checked: shown,
                  disabled: !available,
                  onchange: (e: Event) => {
                    const on = (e.target as HTMLInputElement).checked;
                    const current = new Set(socialLinks(brand, b.networks).map((l) => l.network.id));
                    if (on) current.add(n.id);
                    else current.delete(n.id);
                    const all = socialLinks(brand).map((l) => l.network.id);
                    const next = all.filter((id) => current.has(id));
                    set({ networks: next.length === all.length ? undefined : next } as Partial<LayoutBlock>);
                  },
                }), ` ${n.label}`);
              }),
            ),
            h('small', { class: 'muted' }, 'L’icône « web » pointe vers le site de la charte ; l’ordre est fixe : web, puis les réseaux.'),
          )
          : null,
        b.type === 'text'
          ? (() => {
            const ta = h('textarea', { rows: 4, class: 'ed-textarea', placeholder: 'Votre texte…' }, b.text) as HTMLTextAreaElement;
            ta.addEventListener('input', () => set({ text: ta.value } as Partial<LayoutBlock>, 'text'));
            const insert = (token: string) => {
              const start = ta.selectionStart ?? ta.value.length;
              const end = ta.selectionEnd ?? start;
              ta.value = ta.value.slice(0, start) + token + ta.value.slice(end);
              ta.selectionStart = ta.selectionEnd = start + token.length;
              ta.focus();
              set({ text: ta.value } as Partial<LayoutBlock>, 'text');
            };
            return h(
              'div',
              { class: 'ed-prop' },
              h('span', null, 'Texte'),
              ta,
              h('div', { class: 'ed-inline ed-vars' }, h('small', { class: 'muted' }, 'Insérer :'), TEXT_VARIABLES.map((v) => h('button', { type: 'button', class: 'ghost', title: v.token, onclick: () => insert(v.token) }, v.label))),
              h('small', { class: 'muted' }, 'Les variables restent visibles ici ; le canevas montre les valeurs réelles. Une variable vide disparaît avec sa ponctuation. Retour à la ligne = nouvelle ligne.'),
              row('Rôle typographique', selectEl(textRoleOptions(styles).map((o) => o.id), textRoleOptions(styles).map((o) => o.label), b.role && textRoleOptions(styles).some((o) => o.id === b.role) ? b.role : 'body', (v) => set({ role: v } as Partial<LayoutBlock>)), 'Le texte prend police, taille, graisse et couleur de ce rôle de la charte. Un style personnalisé se crée dans la fiche (« Ajouter un style »). Aucune couleur propre.'),
            );
          })()
          : null,
        paddingRows(b.padding, (p) => set({ padding: p }, 'padding')),
        b.type !== 'social'
          ? row('Lien (URL)', h('input', { type: 'url', value: b.href ?? '', placeholder: 'https://…', onchange: (e: Event) => set({ href: (e.target as HTMLInputElement).value.trim() || undefined }) }), 'Facultatif ; e-mail, téléphones et site ont déjà leur lien')
          : null,
        h('div', { class: 'ed-actions' }, h('button', { type: 'button', class: 'ghost danger', onclick: () => deleteSelection() }, 'Retirer ce bloc')),
      );
      return;
    }
    if (sel.kind === 'col') {
      const loc = findCol(layout(), sel.id);
      if (!loc) return mount(panel, h('p', { class: 'muted' }, 'Colonne introuvable.'));
      const c = loc.col;
      const set = (patch: Partial<LayoutCol>, key?: string) => apply(updateCol(layout(), sel.id, patch), key ? `${sel.id}:${key}` : null);
      const auto = c.width === 'auto';
      const media = mediaColumnWidth(brand, styles, c);
      const labels: Record<BorderSide, string> = { left: 'Gauche', right: 'Droite', top: 'Haut', bottom: 'Bas' };
      const borderRows = (['left', 'right', 'top', 'bottom'] as BorderSide[]).map((side) => {
        const key = `border${side[0].toUpperCase()}${side.slice(1)}` as 'borderLeft' | 'borderRight' | 'borderTop' | 'borderBottom';
        const b = c[key];
        const on = h('input', {
          type: 'checkbox',
          checked: !!b,
          title: b ? 'Retirer ce trait (et l’espacement autour)' : 'Ajouter un trait',
          onchange: (e: Event) => apply((e.target as HTMLInputElement).checked ? setColBorder(layout(), sel.id, side, 1) : removeColBorder(layout(), sel.id, side)),
        });
        const width = h('input', { type: 'number', min: 1, max: 12, value: b?.width ?? 1, disabled: !b, title: 'Épaisseur (px)', oninput: (e: Event) => b && apply(setColBorder(layout(), sel.id, side, Number((e.target as HTMLInputElement).value) || 1), `${sel.id}:${side}:w`) });
        return h('div', { class: 'ed-border' }, h('label', null, on, ' ', labels[side]), width, h('span', { class: 'muted' }, 'px'));
      });
      const hiddenBlocks = c.blocks.filter((b) => !renderedBlocks.has(b.id));
      mount(
        panel,
        h('div', { class: 'ed-panel-title' }, 'Cette colonne', h('small', { class: 'muted' }, ` ${c.blocks.length} bloc(s)`), closeBtn()),
        crumbs(loc.row.id, loc.rowIndex, loc.col.id, loc.colIndex, null),
        media !== null
          ? row(
            'Largeur',
            h('span', { class: 'ed-inline' }, h('input', { type: 'number', value: media, readonly: true, title: 'Calculée, non modifiable ici' }), h('span', { class: 'muted' }, 'px, calculée')),
            `Largeur du logo réglée dans la fiche (${Math.round(brand.logo_width || 145)} px) + marges gauche et droite de la colonne. Pour la changer : la largeur du logo dans la fiche, ou les marges ci-dessous. Rien à saisir, rien à déclarer faux.`,
          )
          : row(
          'Largeur',
          h(
            'span',
            { class: 'ed-inline' },
            // Validée à la fin de la saisie (onchange), pas à chaque touche : sinon le premier chiffre tapé
            // était aussitôt ramené au minimum et enregistré (origine du « 24 px » de la colonne logo de bjbs).
            h('input', { type: 'number', min: colMin(c), max: 800, value: auto ? '' : c.width, placeholder: 'auto', disabled: auto, onchange: (e: Event) => set({ width: Math.max(colMin(c), Math.round(Number((e.target as HTMLInputElement).value) || colMin(c))) }, 'width') }),
            h('label', { class: 'ed-check' }, h('input', { type: 'checkbox', checked: auto, onchange: (e: Event) => set({ width: (e.target as HTMLInputElement).checked ? 'auto' : Math.round(rectOf(nodeFor(sel)!).w) }) }), ' automatique'),
          ),
          `En px, ou automatique (prend la place restante). Poignée sur le bord droit dans le canevas.${columnMinWidth(brand, styles, c) ? ` Contenu : au moins ${columnMinWidth(brand, styles, c)} px (une largeur plus petite serait élargie au rendu).` : ''}`,
          ),
        row('Alignement horizontal', selectEl(['left', 'center', 'right'], ['Gauche', 'Centré', 'Droite'], c.align ?? 'left', (v) => set({ align: v === 'left' ? undefined : (v as LayoutCol['align']) }))),
        row('Alignement vertical', selectEl(['top', 'middle', 'bottom'], ['Haut', 'Milieu', 'Bas'], c.valign ?? 'top', (v) => set({ valign: v as LayoutCol['valign'] }))),
        h('div', { class: 'ed-prop' }, h('span', null, 'Traits'), ...borderRows, h('small', { class: 'muted' }, 'Couleur : rôle « Traits » de la charte. Décocher retire le trait ET l’espacement de part et d’autre.')),
        row(
          'Fond',
          h(
            'span',
            { class: 'ed-inline' },
            h('input', { type: 'color', value: c.backgroundColor ?? '#ffffff', oninput: (e: Event) => set({ backgroundColor: (e.target as HTMLInputElement).value }, 'bg') }),
            h('button', { type: 'button', class: 'ghost', disabled: !c.backgroundColor, title: 'Sans fond', onclick: () => set({ backgroundColor: undefined }) }, 'Aucun'),
          ),
          c.backgroundColor ? c.backgroundColor : 'Transparent',
        ),
        paddingRows(c.padding, (p) => set({ padding: p }, 'padding')),
        hiddenBlocks.length
          ? h(
            'div',
            { class: 'ed-prop' },
            h('span', null, 'Blocs sans donnée pour cette personne'),
            ...hiddenBlocks.map((b) => h('div', { class: 'ed-inline' }, h('span', { class: 'muted' }, blockLabel(b)), h('button', { type: 'button', class: 'ghost', title: 'Retirer', onclick: () => apply(removeBlock(layout(), b.id)) }, '×'))),
            h('small', { class: 'muted' }, 'Ils apparaîtront pour les personnes qui ont la donnée.'),
          )
          : null,
        h('div', { class: 'ed-actions' }, h('button', { type: 'button', class: 'ghost danger', onclick: () => deleteSelection() }, 'Supprimer la colonne')),
      );
      return;
    }
    const rowIndex = layout().rows.findIndex((r) => r.id === sel.id);
    const r = layout().rows[rowIndex];
    mount(
      panel,
      h('div', { class: 'ed-panel-title' }, 'Cette ligne', h('small', { class: 'muted' }, ` ${rowIndex + 1} / ${layout().rows.length}, ${r?.cols.length ?? 0} colonne(s)`), closeBtn()),
      h('div', { class: 'ed-prop' }, h('span', null, 'Colonnes'), h('span', { class: 'ed-inline' }, ...(r?.cols ?? []).map((c, j) => h('button', { type: 'button', class: 'ghost ed-crumb', onclick: () => select({ kind: 'col', id: c.id }) }, `Colonne ${j + 1} (${c.blocks.length})`)))),
      h('p', { class: 'muted' }, 'Glissez un bloc sur le bord d’une colonne pour la scinder ; entre deux lignes pour créer une ligne.'),
      h('div', { class: 'ed-actions' }, h('button', { type: 'button', class: 'ghost danger', onclick: () => deleteSelection() }, 'Supprimer la ligne')),
    );
  };

  refresh();
  return el;
}
